import { HUDLabel, LABEL_ANCHORING } from './HudLabel';

const createIconHost = (iconPath) => {
	return [
		'<svg fill="white" stroke="#666666" width="21" height="26" viewbox="0 0 21 26" xmlns="http://www.w3.org/2000/svg">',
			'<path d="M18.0065 16.8529L18.0066 16.8528C19.4753 15.1219 20.3664 12.8826 20.3664 10.4331C20.3667 4.94732 15.9193 0.5 10.4333 0.5C4.94732 0.5 0.5 4.94732 0.5 10.4333C0.5 12.8829 1.39109 15.1219 2.8598 16.8531L2.85993 16.8532L9.2899 24.4259C9.88895 25.1314 10.9777 25.1313 11.5768 24.4258L18.0065 16.8529Z"/>',
			iconPath || '<circle cx="10.5" cy="10.5" r="7.5"/>',
		'</svg>'
	].join('');
};

/** A HUD Pin is a HUD graphic with only an icon when collapsed and icon + label when expanded. */
export class HUDPinLabel extends HUDLabel {
	constructor(viewer, pos3D, text, labelId, options) {
		super(viewer, pos3D, text, labelId, options);

		const _document = this.viewer.canvasWrap.ownerDocument;
		const placeholder = _document.createElement("div");
		placeholder.innerHTML = createIconHost(options.iconPath);
		this.iconHost = placeholder.firstChild;
		this.iconHost.style.transformOrigin = 'bottom';
		if (!options.iconPath) {
			this.circleEl = this.iconHost.childNodes[1];
		}

		this.container.prepend(this.iconHost);

		this.container.style.display = 'flex';
		this.container.style.borderRadius = '15px';

		this.dragging = false;
		this.inFocus = false;
		this.baseOpacity = 1;
		this.baseZIndex = this.container.style.zIndex;
		this.boundOnMouseOver = this.#onMouseOver.bind(this);
		this.boundOnMouseOut = this.#onMouseOut.bind(this);

		this.setCollapsible(true);

		this.setColor('black');
		if (options.bgColor) {
			this.setBGColor(options.bgColor);
		}

		this.labelAnchor = LABEL_ANCHORING.BOTTOM_MIDDLE;
	}
	
	update() {
		if (this.offsetAnimControl?.isRunning || this.dragging) {
			return;
		}

		// Get canvas position corresponding to this.pos3D
		let {x, y} = this.viewer.impl.worldToClient(this.pos3D);
		x += this.offset.x;
		y += this.offset.y;

		this.pos2D.set(x, y, 0);

		// Hide label if the annotated object is small on screen
		let hidden = !this.visible;

		// Transform the div, so that its center is anchored in (x,y)
		this.container.style.transform = this.getAnchoredTransform(x, y);

		let opacityTarget = 0;

		// If the label should be visible, immediately restore the container visibility, so the fade-in will be displayed.
		// Also update position on screen, set z-index, and reassign opacity target.
		if (!hidden) {
			this.changeContainerVisibility(!hidden);
			this.container.style.zIndex = this.inFocus ? this.baseZIndex + 1000 : this.baseZIndex;
			opacityTarget = this.isCollapsed ? this.visibilityImportance() : 1;
		}

		this.opacityParam.fadeTo(opacityTarget, () => {
			// If the label should be hidden, change container visibility only after the fade-out animation finished.
			// This is needed in order that the element won't be touchable while hidden.
			this.changeContainerVisibility(!hidden);
		});

		// If visibility didn't change, immediately skip to desired opacity.
		if (hidden === this.styleHidden) {
			this.opacityParam.skipAnim();
		}
	}

	setOffset(...args) {
		if (this.dragging) return;

		super.setOffset(...args);
	}

	/** Collapse the pin to an icon only */
	#setCollapsed(wantsCollapsed) {
		if (this.isCollapsed === wantsCollapsed) return;

		if (wantsCollapsed) {
			this.container.style.boxShadow = 'none';
			this.container.style.border = 'none';
			super.setBGColor('transparent');

			this.textDiv.style.display = 'none';
			this.iconHost.style.display = 'block'
		} else {
			// important when showing icons only as they often overlap.
			this.container.style.boxShadow = '0 0 7px rgba(0,0,0,1.0)';
			this.container.style.border = '3px solid white';
			super.setBGColor(this.backgroundColor);

			this.textDiv.style.display = 'block';
			this.iconHost.style.display = 'none'
		}

		// The pin may be collapsed even if it is not collapsible (e.g. when dragging)
		this.isCollapsed = wantsCollapsed;
	}

	setCollapsed(wantsCollapsed) {
		if (!this.isCollapsible) return;

		this.#setCollapsed(wantsCollapsed);
	}

	setBGColor(color) {
		this.backgroundColor = color;
		super.setBGColor(this.isCollapsed ? 'transparent' : this.backgroundColor);
		if (this.circleEl) {
			this.circleEl.setAttribute('fill', this.backgroundColor);
		}
	}

	/** Sets whether the pin should be only an icon when not hovered */
	setCollapsible(wantsCollapsible) {
		if (this.isCollapsible === wantsCollapsible) return;

		if (wantsCollapsible) {
			this.addEventListener(HUDLabel.Events.ON_MOUSE_OVER, this.boundOnMouseOver);
			this.addEventListener(HUDLabel.Events.ON_MOUSE_OUT, this.boundOnMouseOut);
		} else {
			this.removeEventListener(HUDLabel.Events.ON_MOUSE_OVER, this.boundOnMouseOver);
			this.removeEventListener(HUDLabel.Events.ON_MOUSE_OUT, this.boundOnMouseOut);
		}

		this.isCollapsible = true;
		this.setCollapsed(wantsCollapsible);
		this.isCollapsible = wantsCollapsible;
	}

	dtor() {
		if (this.isCollapsible) {
			this.removeEventListener(HUDLabel.Events.ON_MOUSE_OVER, this.boundOnMouseOver);
			this.removeEventListener(HUDLabel.Events.ON_MOUSE_OUT, this.boundOnMouseOut);
		}

		super.dtor();
	}

	#onMouseOver({ event }) {
		event.preventDefault();
		event.stopPropagation();

		this.container.style.zIndex = this.baseZIndex + 1000;

		this.opacityParam.fadeTo(1);

		this.setCollapsed(false);
		this.inFocus = true;
	}

	#onMouseOut({ event }) {
		event.preventDefault();
		event.stopPropagation();

		this.container.style.zIndex = this.baseZIndex;

		this.opacityParam.fadeTo(this.baseOpacity);
		
		this.setCollapsed(true);
		this.inFocus = false;
	}

	clone() {
		const clone = new HUDPinLabel(this.viewer, this.pos3D, this.text, this.labelId, {
			iconPath: this.iconPath,
			isWorldScaled: this.isWorldScaled,
			bgColor: this.backgroundColor
		});

		clone.setElement(this.element);
		clone.setDraggable(this.isDraggable);
		clone.container.style.cursor = this.container.style.cursor;

		return clone;
	}

	dragStart() {
		this.dragging = true;
		this.#setCollapsed(true);

		if (this.offsetAnimControl?.isRunning) {
			this.offsetAnimControl.skip();
		}

		if (this.isCollapsible) {
			this.removeEventListener(HUDLabel.Events.ON_MOUSE_OVER, this.boundOnMouseOver);
			this.removeEventListener(HUDLabel.Events.ON_MOUSE_OUT, this.boundOnMouseOut);
		}
	}

	dragMove(x, y) {
		if (this.dragging) {
			this.container.style.transform = this.getAnchoredTransform(x, y + 20);
		}
	}

	dragStop() {
		this.setCollapsible(this.isCollapsible);
		this.dragging = false;
	}
}
